import React, { useEffect, useState } from "react";
import { api } from "../lib/api.js";

// Operator panel for the two mission-file surfaces:
//   · section B, react_items + the reactions that come back
//   · section A, read_blocks
//
// Everything is created unpublished. Nothing reaches the participant until someone
// presses "publish" on that specific row — which is what lets the portfolio numbers
// sit finished-but-dark until the workshop opens them.

const KINDS = ["post", "mirror", "analysis", "number", "claim"];

const EMPTY_ITEM = {
  kind: "post", category: "", ord: 0,
  title: "", author: "", url: "", excerpt: "", operator_note: "",
};
const EMPTY_BLOCK = { section: "A", category: "", ord: 0, title: "", body: "", source_note: "" };

export default function MissionFile({ participantId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.adminReact(participantId).then(setD).catch((e) => setErr(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [participantId]);

  if (err) return <div className="qf-card"><p className="field-err">{err}</p></div>;
  if (!d) return <div className="qf-card"><p className="muted-sm">Loading the mission file…</p></div>;

  const live = d.items.filter((i) => i.visible).length;
  const answered = d.items.filter((i) => i.response).length;

  return (
    <>
      <div className="qf-card">
        <div className="hstack qf-between">
          <div className="qf-label qf-label--accent">
            Section B · React if you want
            <span className="muted-sm"> {live} of {d.items.length} published · {answered} reacted to</span>
          </div>
          <TranscribeReactions id={participantId} onDone={load} />
        </div>

        <div style={{ marginTop: "var(--qf-3)" }}>
          {d.items.length === 0 && <p className="muted-sm">No material loaded yet.</p>}
          {d.items.map((it) => (
            <ItemRow key={it.id} item={it} participantId={participantId} onChange={load} />
          ))}
        </div>

        <NewItem participantId={participantId} onChange={load}
          categories={[...new Set(d.items.map((i) => i.category))]} />
      </div>

      <div className="qf-card">
        <div className="qf-label qf-label--accent">
          Section A · What I've read
          <span className="muted-sm"> {d.blocks.filter((b) => b.visible).length} of {d.blocks.length} published</span>
        </div>
        <div style={{ marginTop: "var(--qf-3)" }}>
          {d.blocks.length === 0 && <p className="muted-sm">Nothing written yet.</p>}
          {d.blocks.map((b) => (
            <BlockRow key={b.id} block={b} onChange={load} />
          ))}
        </div>
        <NewBlock participantId={participantId} onChange={load}
          categories={[...new Set(d.blocks.map((b) => b.category))]} />
      </div>
    </>
  );
}

// ---- One react item ---------------------------------------------------------
function ItemRow({ item, participantId, onChange }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [operating, setOperating] = useState(false);

  const patch = async (p) => {
    setBusy(true);
    try { await api.adminReactSave({ id: item.id, patch: p }); onChange(); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirm(`Delete "${item.title || item.category}" and any reaction to it?`)) return;
    setBusy(true);
    try { await api.adminReactDelete(item.id, "item"); onChange(); }
    finally { setBusy(false); }
  };

  const r = item.response;

  return (
    <div className="mf-row">
      <span className={`mf-live ${item.visible ? "mf-live--on" : "mf-live--off"}`}>
        {item.visible ? "live" : "draft"}
      </span>

      <div className="mf-row__main">
        <div className="mf-row__t">{item.title || <em className="muted-sm">untitled</em>}</div>
        <div className="mf-row__s">
          {item.kind} · {item.category} · ord {item.ord}
          {item.author ? ` · ${item.author}` : ""}
          {item.url ? <> · <a href={item.url} target="_blank" rel="noreferrer">original</a></> : null}
        </div>
        {!item.excerpt && (
          <div className="mf-row__s" style={{ color: "#b23" }}>
            No excerpt — the card will read as empty to the participant.
          </div>
        )}

        {r && (
          <div className="mf-resp">
            <div>
              <strong>{r.verdict || "no verdict"}</strong>
              {r.skipped ? " · skipped" : ""}
              {r.source === "operator" ? " · captured by you in session" : ""}
            </div>
            {r.note_text && <div style={{ whiteSpace: "pre-wrap" }}>{r.note_text}</div>}
            {r.transcript_text && <div><em>Transcript:</em> {r.transcript_text}</div>}
            {r.audio_url && <audio controls src={r.audio_url} />}
          </div>
        )}

        {editing && <EditItem item={item} onDone={() => { setEditing(false); onChange(); }} />}
        {operating && (
          <OperatorResponse item={item} participantId={participantId}
            onDone={() => { setOperating(false); onChange(); }} />
        )}
      </div>

      <div className="mf-row__acts">
        <button className="link-btn" disabled={busy} onClick={() => patch({ visible: item.visible ? 0 : 1 })}>
          {item.visible ? "unpublish" : "publish"}
        </button>
        <button className="link-btn" disabled={busy} onClick={() => patch({ ord: item.ord - 1 })}>↑</button>
        <button className="link-btn" disabled={busy} onClick={() => patch({ ord: item.ord + 1 })}>↓</button>
        <button className="link-btn" onClick={() => setEditing((v) => !v)}>edit</button>
        <button className="link-btn" onClick={() => setOperating((v) => !v)}>answer for them</button>
        <button className="link-btn" disabled={busy} onClick={remove}>delete</button>
      </div>
    </div>
  );
}

function EditItem({ item, onDone }) {
  const [f, setF] = useState({ ...EMPTY_ITEM, ...item });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.adminReactSave({
        id: item.id,
        patch: {
          kind: f.kind, category: f.category, ord: Number(f.ord) || 0, title: f.title,
          author: f.author, url: f.url, excerpt: f.excerpt, operator_note: f.operator_note,
        },
      });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className="mf-form">
      <ItemFields f={f} set={set} setF={setF} />
      <div className="hstack">
        <button className="qf-btn qf-btn--primary" disabled={busy} onClick={save}>Save</button>
        <button className="link-btn" onClick={onDone}>cancel</button>
      </div>
    </div>
  );
}

function ItemFields({ f, set, setF, categories = [] }) {
  return (
    <>
      <div className="mf-form__grid">
        <select className="input" value={f.kind} onChange={set("kind")}>
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <input className="input" placeholder="Category (the grouping he sees)" list="mf-cats"
          value={f.category} onChange={set("category")} />
        <datalist id="mf-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        <input className="input" type="number" placeholder="Order" value={f.ord} onChange={set("ord")} />
      </div>
      <div className="mf-form__grid">
        <input className="input" placeholder="Title" value={f.title} onChange={set("title")} />
        <input className="input" placeholder="Author" value={f.author} onChange={set("author")} />
      </div>
      <input className="input" placeholder="URL to the original" value={f.url} onChange={set("url")} />
      <textarea className="input" placeholder="Excerpt — 2 to 4 lines, pasted by hand. LinkedIn can't be fetched server-side."
        value={f.excerpt} onChange={set("excerpt")} style={{ minHeight: 80 }} />
      <textarea className="input" placeholder="What you want him to react to"
        value={f.operator_note} onChange={set("operator_note")} style={{ minHeight: 60 }} />
    </>
  );
}

function NewItem({ participantId, onChange, categories }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY_ITEM);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async () => {
    if (!f.category.trim()) return;
    setBusy(true);
    try {
      await api.adminReactSave({
        participant_id: participantId,
        item: { ...f, ord: Number(f.ord) || 0, visible: 0 },
      });
      setF(EMPTY_ITEM); setOpen(false); onChange();
    } finally { setBusy(false); }
  };
  if (!open) return <button className="link-btn" style={{ marginTop: "var(--qf-3)" }} onClick={() => setOpen(true)}>+ add material</button>;
  return (
    <div className="qf-card qf-card--quiet mf-form" style={{ marginTop: "var(--qf-3)" }}>
      <ItemFields f={f} set={set} setF={setF} categories={categories} />
      <div className="hstack">
        <button className="qf-btn qf-btn--primary" disabled={busy} onClick={add}>Add as draft</button>
        <button className="link-btn" onClick={() => setOpen(false)}>close</button>
      </div>
      <p className="muted-sm">Created unpublished. Press "publish" on the row when it's ready for him.</p>
    </div>
  );
}

// The reaction captured live in the workshop, typed by the operator.
function OperatorResponse({ item, participantId, onDone }) {
  const verdicts = Array.isArray(item.verdict_set) && item.verdict_set.length
    ? item.verdict_set
    : ["I could do this, and I want to", "I could do this, but I don't want to", "Not me", "Not sure"];
  const [verdict, setVerdict] = useState(item.response?.verdict || "");
  const [note, setNote] = useState(item.response?.note_text || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.adminReactSave({
        participant_id: participantId, item_id: item.id,
        operator_response: { verdict: verdict || null, note_text: note || null },
      });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className="mf-form">
      <select className="input" value={verdict} onChange={(e) => setVerdict(e.target.value)}>
        <option value="">— what he said —</option>
        {verdicts.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
      <textarea className="input" placeholder="What he actually said, in his words"
        value={note} onChange={(e) => setNote(e.target.value)} style={{ minHeight: 70 }} />
      <div className="hstack">
        <button className="qf-btn qf-btn--primary" disabled={busy} onClick={save}>Record his reaction</button>
        <button className="link-btn" onClick={onDone}>cancel</button>
      </div>
    </div>
  );
}

// ---- Section A blocks -------------------------------------------------------
function BlockRow({ block, onChange }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const patch = async (p) => {
    setBusy(true);
    try { await api.adminReactSave({ id: block.id, target: "block", patch: p }); onChange(); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirm(`Delete "${block.title || block.category}"?`)) return;
    setBusy(true);
    try { await api.adminReactDelete(block.id, "block"); onChange(); }
    finally { setBusy(false); }
  };
  return (
    <div className="mf-row">
      <span className={`mf-live ${block.visible ? "mf-live--on" : "mf-live--off"}`}>
        {block.visible ? "live" : "draft"}
      </span>
      <div className="mf-row__main">
        <div className="mf-row__t">{block.title || <em className="muted-sm">untitled</em>}</div>
        <div className="mf-row__s">{block.category} · ord {block.ord}{block.source_note ? ` · ${block.source_note}` : ""}</div>
        {editing && <EditBlock block={block} onDone={() => { setEditing(false); onChange(); }} />}
      </div>
      <div className="mf-row__acts">
        <button className="link-btn" disabled={busy} onClick={() => patch({ visible: block.visible ? 0 : 1 })}>
          {block.visible ? "unpublish" : "publish"}
        </button>
        <button className="link-btn" disabled={busy} onClick={() => patch({ ord: block.ord - 1 })}>↑</button>
        <button className="link-btn" disabled={busy} onClick={() => patch({ ord: block.ord + 1 })}>↓</button>
        <button className="link-btn" onClick={() => setEditing((v) => !v)}>edit</button>
        <button className="link-btn" disabled={busy} onClick={remove}>delete</button>
      </div>
    </div>
  );
}

function BlockFields({ f, set, categories = [] }) {
  return (
    <>
      <div className="mf-form__grid">
        <input className="input" placeholder="Category (group heading)" list="mf-bcats"
          value={f.category} onChange={set("category")} />
        <datalist id="mf-bcats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        <input className="input" type="number" placeholder="Order" value={f.ord} onChange={set("ord")} />
      </div>
      <input className="input" placeholder="Title" value={f.title} onChange={set("title")} />
      <textarea className="input" placeholder="Body. Blank line between paragraphs."
        value={f.body} onChange={set("body")} style={{ minHeight: 110 }} />
      <input className="input" placeholder="Source, e.g. LP report 2025, portfolio tables"
        value={f.source_note} onChange={set("source_note")} />
    </>
  );
}

function EditBlock({ block, onDone }) {
  const [f, setF] = useState({ ...EMPTY_BLOCK, ...block });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.adminReactSave({
        id: block.id, target: "block",
        patch: { category: f.category, ord: Number(f.ord) || 0, title: f.title, body: f.body, source_note: f.source_note },
      });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className="mf-form">
      <BlockFields f={f} set={set} />
      <div className="hstack">
        <button className="qf-btn qf-btn--primary" disabled={busy} onClick={save}>Save</button>
        <button className="link-btn" onClick={onDone}>cancel</button>
      </div>
    </div>
  );
}

function NewBlock({ participantId, onChange, categories }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY_BLOCK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async () => {
    if (!f.category.trim()) return;
    setBusy(true);
    try {
      await api.adminReactSave({ participant_id: participantId, block: { ...f, ord: Number(f.ord) || 0, visible: 0 } });
      setF(EMPTY_BLOCK); setOpen(false); onChange();
    } finally { setBusy(false); }
  };
  if (!open) return <button className="link-btn" style={{ marginTop: "var(--qf-3)" }} onClick={() => setOpen(true)}>+ add a block</button>;
  return (
    <div className="qf-card qf-card--quiet mf-form" style={{ marginTop: "var(--qf-3)" }}>
      <BlockFields f={f} set={set} categories={categories} />
      <div className="hstack">
        <button className="qf-btn qf-btn--primary" disabled={busy} onClick={add}>Add as draft</button>
        <button className="link-btn" onClick={() => setOpen(false)}>close</button>
      </div>
    </div>
  );
}

function TranscribeReactions({ id, onDone }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const run = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await api.adminReactTranscribe(id);
      setNote(r.total === 0 ? "Nothing waiting" : `${r.transcribed} of ${r.total} transcribed`);
      onDone();
    } catch (e) { setNote(e.message || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <span className="hstack" style={{ gap: ".5rem" }}>
      <button className="qf-btn qf-btn--ghost" onClick={run} disabled={busy}>
        {busy ? "Transcribing…" : "Transcribe reactions"}
      </button>
      {note && <span className="muted-sm">{note}</span>}
    </span>
  );
}
